import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { loadStripe, StripeError } from '@stripe/stripe-js';

import config from 'config';
import { apolloClient } from 'common/graphql/clients';
import useCompanyContext from 'common/hooks/useCompanyContext';
import { removeTypenames } from 'helpers/graphql';

import { CreateMerchantAccountDocument } from './graphql/createMerchantAccount.gql';
import { MerchantOnboardingInfoDocument } from './graphql/merchantOnboardingInfoQuery.gql';

export const useCreateMerchantAccountMutation = () => {
  const { companyProfileId } = useCompanyContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | StripeError | undefined>(
    undefined,
  );

  if (!companyProfileId) {
    throw new Error(
      'companyProfileId is undefined in useCreateMerchantAccount',
    );
  }

  const [mutation] = useMutation(CreateMerchantAccountDocument, {
    update: (cache, { data }) => {
      if (!data) {
        return;
      }

      const {
        createMerchantAccount: { merchantAccount },
      } = data;

      cache.modify({
        fields: {
          merchantAccount() {
            return merchantAccount;
          },
        },
        id: cache.identify({
          __typename: `Company`,
          companyProfileId: merchantAccount.companyProfileId,
        }),
      });
    },
  });

  const createCompanyPaymentProviderAccount = async () => {
    try {
      setLoading(true);
      const {
        data: {
          viewer: {
            company: { merchantOnboardingInfo },
          },
        },
      } = await apolloClient.query({
        query: MerchantOnboardingInfoDocument,
        variables: { companyProfileId },
      });

      const { account, persons } = removeTypenames(merchantOnboardingInfo);

      const stripe = await loadStripe(config.newPspStripeKey);

      if (!stripe) {
        setError(new Error('An error occurred while loading the Stripe SDK'));
        return;
      }

      const { error: accountTokenError, token: accountToken } =
        await stripe.createToken('account', {
          ...account,
          business_type: account.business_type as 'company' | 'individual',
          tos_shown_and_accepted: true,
        });

      const { personsTokens, personTokenError } = (
        await Promise.all(
          persons.map(({ phone, ...person }) =>
            stripe.createToken('person', {
              ...person,
              ...(phone && { phone }),
            }),
          ),
        )
      ).reduce<{
        personTokenError: StripeError | undefined;
        personsTokens: string[];
      }>(
        (acc, { error: tokenError, token }) => {
          if (tokenError) {
            return { ...acc, personTokenError: tokenError };
          }

          if (!token) {
            return acc;
          }

          return {
            ...acc,
            personsTokens: [...acc.personsTokens, token.id],
          };
        },
        {
          personsTokens: [],
          personTokenError: undefined,
        },
      );

      const stripeError = accountTokenError ?? personTokenError;

      if (stripeError ?? !accountToken) {
        setError(
          stripeError ??
            new Error('An error occurred while creating the account token'),
        );
        return;
      }

      await mutation({
        variables: {
          input: {
            companyProfileId,
            tokens: {
              account: accountToken.id,
              persons: personsTokens,
            },
          },
        },
      });
    } catch (err) {
      if (err instanceof Error) {
        setError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  return [createCompanyPaymentProviderAccount, { error, loading }] as const;
};
